import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ConfirmationDialogComponent, ErrorNavigatorComponent } from '@app/components';
import { ErrorNavigatorBeforeChange } from '@app/components/error-navigator/error-navigator.component';
import { AnalyticEvents } from '@app/enums/analytic-events.enum';
import { ColumnType } from '@app/modules/import/interfaces';
import { AnalyticService, AuthService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { Column } from '@import/classes';
import { InputType } from '@import/enums/input-type.enum';
import { ValidationState } from '@import/enums/validation-state.enum';
import { getColumnValueFromRow, getColumnValuesFromRows, getRowErrorCount } from '@import/functions/rows';
import { ProcessedRow } from '@import/interfaces/processed-row.interface';
import { EmployeeHumiColumnService, EmployeeRowAndColumnService, ReviewErrorService } from '@import/services';
import { Role } from '@models/account/role.model';
import { Office } from '@models/company/office.model';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import stickybits from 'stickybits';

const ITEMS_PER_PAGE = 50;
const DEBOUNCE_TIME = 500;

interface RowPagination {
    current: number;
    limit: number;
    total: number;
}

@Component({
    selector: 'app-form-employees-import-review',
    templateUrl: './review.template.html',
    styleUrls: ['./review.style.scss', './table.style.scss'],
})
/**
 * Refactor notes: Custom field errors pertain to the employee importer. The formatting of these errors can possibly be moved out to the
 * column classes in the future so they can determine how to display their error messages.
 *
 * The retrieval of roles/offices should also be moved to a parent service/view.
 */
export class ReviewForm extends BaseForm implements OnInit, OnDestroy {
    @ViewChild('removeModal', { static: true }) removeModal: ConfirmationDialogComponent;
    @ViewChild('errorNavigator') errorNavigator: ErrorNavigatorComponent;
    @Output() isLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() fullscreenEnabled: EventEmitter<boolean> = new EventEmitter<boolean>();
    shouldRenderEmployeeRows = false;
    emailToBeRemoved = '';
    isFullscreenEnabled = false;
    hasPagination = false;
    InputType = InputType;
    ValidationState = ValidationState;
    rowPagination: RowPagination = {
        current: 1,
        total: 0,
        limit: ITEMS_PER_PAGE,
    };
    protected formControlSubscriptions: Subscription[] = [];

    constructor(
        public importerService: EmployeeRowAndColumnService,
        public columnService: EmployeeHumiColumnService,
        public reviewErrorService: ReviewErrorService,
        private auth: AuthService,
        private element: ElementRef,
        private analytics: AnalyticService
    ) {
        super();
    }

    getPageUpperLimit(): number {
        return this.rowPagination.limit * (this.rowPagination.current - 1);
    }

    async ngOnInit(): Promise<void> {
        this.isLoading.emit(true);
        await Promise.all(this.getColumnTypeOptions());

        this.columnService.updateMappedColumns();

        if (this.importerService.shouldProcessRows()) {
            this.importerService.setPrimaryColumnByType(this.columnService.getColumnType('account.email'));
            this.importerService.processRows();
        }

        const primaryIndex = this.importerService.primaryColumn.csvColumn.index;
        const initialEmails = getColumnValuesFromRows(this.importerService.processedRows, primaryIndex);

        await this.prepareValidationErrors(initialEmails);

        this.registerSubscriptions(primaryIndex);
        this.rowPagination.total = this.importerService.processedRows.length;
        this.hasPagination = this.rowPagination.total / this.rowPagination.limit > 1;
        this.domActions();
        this.isLoading.emit(false);
    }

    ngOnDestroy(): void {
        this.importerService.processedRows = [];
        this.formControlSubscriptions.forEach((sub: Subscription) => {
            sub.unsubscribe();
        });
    }

    getColumnTypeOptions(): Promise<any>[] {
        return this.columnService
            .getMappedCsvColumns()
            .filter((column: Column) => column.type && column.type.input === InputType.options)
            .map((column: Column) => this.refreshOptions(column));
    }

    refreshOptions(column: Column): Promise<any> {
        if (!column.type || column.type.input !== InputType.options) {
            return;
        }

        switch (column.type.label) {
            case 'Office':
                return this.getOfficeOptions(column);
            case 'Role':
                return this.getRoleOptions(column);
            default:
                break;
        }
    }

    handleBeforeErrorChange($event: ErrorNavigatorBeforeChange): void {
        if ($event.previousNonErrorInput) {
            this.errorNavigator.activeErrorIndex = this.reviewErrorService.findClosestError($event);
        }
        const pageWithError = this.reviewErrorService.getPageToPaginateTo(
            this.errorNavigator.activeErrorIndex,
            ITEMS_PER_PAGE
        );

        if (pageWithError !== this.rowPagination.current) {
            this.changePage(pageWithError);
        }
    }

    rowHasError(row: ProcessedRow): boolean {
        return getRowErrorCount(row) !== 0;
    }

    removeRow(row: ProcessedRow): void {
        this.emailToBeRemoved = this.importerService.getRowPrimaryValue(row) + '';

        this.removeModal.open().then((choice: boolean) => {
            if (!choice) {
                return;
            }

            this.importerService.removeProcessedRow(row);
            this.reviewErrorService.buildErrors(this.importerService.processedRows);
            this.updatePagination();
            this.domActions();
            this.analytics.trackEvent(AnalyticEvents.ImporterRowDeleted);
        });
    }

    changePage(newPage: number): void {
        this.isLoading.emit(true);
        setTimeout(() => {
            this.rowPagination.current = newPage;
            this.domActions();
            setTimeout(() => {
                this.isLoading.emit(false);
                this.errorNavigator.focusOnError();
            });
        });
    }

    updateCustomFieldErrors(row: ProcessedRow): void {
        Object.entries(row.errors)
            .map((error: Array<string>) => {
                if (error[1] === 'Invalid custom field') {
                    return error[0];
                }
            })
            .forEach((index: string) => {
                const errorColumn: ColumnType = this.columnService
                    .getMappedHumiColumns()
                    .find((humiColumn: ColumnType) => humiColumn.csvColumn.index === Number(index));

                if (!errorColumn) {
                    return;
                }

                const errorValue = row.raw[index];

                // Is required?
                if (errorColumn.required && (!errorValue || errorValue === '')) {
                    row.errors[index] = errorColumn.csvColumn.name + ' is required.';

                    return;
                }

                // Is boolean?
                if (errorColumn.input === InputType.boolean) {
                    if (typeof errorValue === InputType.boolean) {
                        row.errors[index] = 'Must be yes or no.';
                    }

                    return;
                }

                // Is it actually an integer as expected?
                const isIntegerRule = errorColumn.input === InputType.integer;
                if (isIntegerRule) {
                    if (!parseInt(errorValue, 10)) {
                        row.errors[index] = 'Must be a number.';
                        return;
                    }
                }

                // Min/max checks
                const getRuleValue = (key: string) => errorColumn.rules.find((rule) => rule.includes(key));
                const max = getRuleValue('max');
                const min = getRuleValue('min');
                const [append, value] = isIntegerRule ? ['.', errorValue] : [' characters.', errorValue.length];

                let errorCode = 0;
                if (max) {
                    errorCode += value > max[1] ? 1 : 0;
                }
                if (min) {
                    errorCode += value < min[1] ? 2 : 0;
                }

                if (!errorCode) {
                    return;
                }

                const errorMessages = {
                    1: `Must be less than ${max}` + append,
                    2: `Must be more than ${min}` + append,
                    3: `Must be between ${min} and ${max}` + append,
                };

                row.errors[index] = errorMessages[errorCode];
            });
    }

    toggleFullscreen(): void {
        this.isFullscreenEnabled = !this.isFullscreenEnabled;
        if (this.isFullscreenEnabled) {
            this.analytics.trackEvent(AnalyticEvents.ImporterStep4FullScreenOpened);
        } else {
            this.analytics.trackEvent(AnalyticEvents.ImporterStep4FullScreenClosed);
        }
        this.fullscreenEnabled.emit(this.isFullscreenEnabled);
    }

    handleNavigationToNextError(): void {
        this.analytics.trackEvent(AnalyticEvents.ImporterStep4NavigateToNextError);
    }
    handleNavigationToPreviousError(): void {
        this.analytics.trackEvent(AnalyticEvents.ImporterStep4NavigateToPreviousError);
    }

    protected updatePagination(): void {
        this.rowPagination.total = this.importerService.processedRows.length;
        const newPageCount = Math.ceil(this.rowPagination.total / ITEMS_PER_PAGE);

        if (this.rowPagination.current > newPageCount) {
            this.rowPagination.current = newPageCount;
        }
    }

    protected domActions(): void {
        this.sticky();
        this.addBorderDom();
    }

    protected sticky(): void {
        setTimeout(() => {
            stickybits('.left-sticky');
            stickybits('.top-sticky');
        });
    }

    protected addBorderDom(): void {
        setTimeout(() => {
            // Acts as a "buffer" before the border classes are applied
            const cssClass = 'separator';
            // Need to set the height of the sentinel to at least cover the entire form/table
            const form = this.element.nativeElement.querySelector('form');
            const leftSentinel = this.element.nativeElement.querySelector('div.left-sentinel');
            leftSentinel.style.height = form.offsetHeight + 'px';
            const stickyColumn = form.querySelectorAll('td.left-sticky:not(.status)');

            form.addEventListener('scroll', () => {
                if (leftSentinel.getBoundingClientRect().left < form.getBoundingClientRect().left) {
                    stickyColumn.forEach((td) => td.classList.add(cssClass));
                } else {
                    stickyColumn.forEach((td) => td.classList.remove(cssClass));
                }
            });
        });
    }

    private totalErrorsAnalytics(): void {
        let errorRowCount = 0;
        let totalErrorCount = 0;
        this.importerService.processedRows.forEach((row: ProcessedRow) => {
            const errorCount = Object.keys(row.errors).length;
            if (errorCount) {
                errorRowCount++;
                totalErrorCount += errorCount;
            }
        });

        this.analytics.trackEvent(AnalyticEvents.ImporterErrorCount, {
            errorRows: errorRowCount,
            totalErrors: totalErrorCount,
        });
    }

    private getOfficeOptions(column: Column): Promise<any> {
        return Office.param('company', this.auth.company.id)
            .all()
            .then(([offices]) => {
                column.type.options = offices.map((office) => ({
                    label: office.name,
                    value: office.id,
                }));
            });
    }

    private getRoleOptions(column: Column): Promise<any> {
        return Role.where('companyId', this.auth.company.id)
            .all()
            .then(([roles]) => {
                column.type.options = roles.map((role) => ({
                    label: role.name,
                    value: role.id,
                }));
            });
    }

    private registerSubscriptions(primaryIndex: number): void {
        const reportsToIndex: number | null = this.columnService.getIndexBySerializeName('reportsToEmail');

        this.formControlSubscriptions = this.importerService.processedRows.map((row: ProcessedRow) => {
            let previousEmail = getColumnValueFromRow(row, primaryIndex);
            return row.formControls.valueChanges.pipe(debounceTime(DEBOUNCE_TIME)).subscribe((changes) => {
                const errorsBeforeValidation = getRowErrorCount(row);
                row.raw = row.raw.map((_, index: number) => changes[index]);

                let forValidation: ProcessedRow[] = [row];

                // If row email has changed, revalidate any rows that report to this employees new, or old, email
                const newEmail = getColumnValueFromRow(row, primaryIndex);
                if (newEmail !== previousEmail) {
                    forValidation = forValidation.concat(
                        this.findDirectReportRowsForValidation(reportsToIndex, [previousEmail, newEmail])
                    );
                }
                previousEmail = newEmail;

                // on validation, send all emails currently being imported
                const currentEmails = getColumnValuesFromRows(this.importerService.processedRows, primaryIndex);
                forValidation.forEach((row: ProcessedRow) => {
                    this.importerService.validateRow(row, currentEmails).then(() => {
                        this.reviewErrorService.buildErrors(this.importerService.processedRows);
                        if (errorsBeforeValidation && getRowErrorCount(row) < errorsBeforeValidation) {
                            this.analytics.trackEvent(AnalyticEvents.ImporterRowErrorCorrected);
                        }
                        this.updateCustomFieldErrors(row);
                    });
                });
            });
        });
    }

    private async prepareValidationErrors(emails: string[]): Promise<void> {
        await Promise.all(
            this.importerService.processedRows.map((row: ProcessedRow) =>
                this.importerService.validateRow(row, emails).then(() => {
                    this.updateCustomFieldErrors(row);
                })
            )
        );
        this.shouldRenderEmployeeRows = true;
        this.totalErrorsAnalytics();
        this.reviewErrorService.buildErrors(this.importerService.processedRows);
    }

    private findDirectReportRowsForValidation(reportsToIndex: null | number, emails: string[]): ProcessedRow[] {
        if (reportsToIndex) {
            return this.importerService.processedRows.filter((row: ProcessedRow) =>
                emails.includes(getColumnValueFromRow(row, reportsToIndex))
            );
        }
        return [];
    }
}
